/** Real customer-style artwork for the Artwork billboard. Files live in /public/assets/art. */

const art = (key, title, format, note, fit = "contain") => ({ key, src: `/assets/art/${key}.webp`, title, format, note, fit });

/** The rest of the wall — taped-up prints behind the sign. Some hang off the edge on purpose. */
const WALL = [
    { ...art("reunion", "Family reunion", "PDF"), left: "20%", top: "-2%", w: 300, tilt: 2, tape: true },
    { ...art("grad", "Class of 2026", "EPS"), left: "60%", top: "-3%", w: 200, tilt: -5, tape: true, mobile: true },
    { ...art("bakery", "Bakery logo", "PDF"), left: "-2%", top: "3%", w: 230, tilt: -6, tape: true, mobile: true },
    { ...art("coney", "Retro sunset", "AI"), right: "-2%", top: "5%", w: 215, tilt: 5, tape: true },
    { ...art("skull", "Skull and roses", "PNG"), left: "1%", top: "36%", w: 265, tilt: 4 },
    { ...art("bridge", "Photo print", "JPG"), right: "-3%", top: "34%", w: 285, tilt: -4, fit: "cover" },
    { ...art("blessed", "Faith tee", "SVG"), left: "-2%", top: "68%", w: 245, tilt: -3, tape: true },
    { ...art("frenchie", "Pet portrait", "JPG"), right: "1%", top: "70%", w: 200, tilt: 6, fit: "cover", mobile: true },
    { ...art("ballers", "Team logo", "EPS"), left: "42%", top: "90%", w: 205, tilt: 3, tape: true },
    { ...art("bride", "Bride tribe", "AI"), left: "10%", top: "92%", w: 210, tilt: -4 },
    { ...art("sweet16", "Sweet 16", "AI"), left: "40%", top: "-6%", w: 240, tilt: -3, tape: true },
    { ...art("trail", "Trail tee", "SVG"), left: "27%", top: "86%", w: 200, tilt: 4, tape: true },
    { ...art("type", "Lettering", "PSD"), left: "58%", top: "91%", w: 250, tilt: -2 },
    { ...art("abstract", "Abstract print", "JPG"), left: "16%", top: "44%", w: 200, tilt: 5, fit: "cover" },
    { ...art("mascot", "Shop mascot", "PNG"), left: "12%", top: "60%", w: 95, tilt: 0, cutout: true, mobile: true },
];

/** A little file-type sticker, the kind we slap on a proof. */
export function FormatTag({ format, className = "" }) {
    return (
        <span className={`inline-flex items-center rounded-sm bg-zinc-900 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-white ${className}`}>
            .{format}
        </span>
    );
}

function Tape({ side }) {
    return (
        <span className={`absolute -top-2 h-4 w-12 rotate-[-4deg] bg-[#f5e9b8]/85 shadow-sm ${side === "l" ? "-left-3 rotate-[-12deg]" : "-right-3 rotate-[10deg]"}`} />
    );
}

/** A print taped to the wall — a thin paper border, a soft shadow and its file type. A cutout has no paper at all. */
function Print({ p }) {
    return (
        <div data-testid={`art-wallpaper-${p.key}`}
            className={`absolute ${p.mobile ? "" : "hidden md:block"}`}
            style={{ left: p.left, right: p.right, top: p.top, width: p.w, transform: `rotate(${p.tilt}deg)` }}>
            {p.cutout ? (
                <div className="relative">
                    <img src={p.src} alt={p.title} loading="lazy" className="block w-full drop-shadow-[0_18px_22px_rgba(0,0,0,0.45)]" />
                    <FormatTag format={p.format} className="absolute -right-3 bottom-4" />
                </div>
            ) : (
            <div className="relative border-[6px] border-white bg-white shadow-[0_22px_40px_-20px_rgba(0,0,0,0.55)]">
                {p.tape && <><Tape side="l" /><Tape side="r" /></>}
                <img src={p.src} alt={p.title} loading="lazy"
                    className={`block w-full ${p.fit === "cover" ? "aspect-[4/3] object-cover" : "max-h-[300px] object-contain"}`} />
                <FormatTag format={p.format} className="absolute bottom-1.5 right-1.5" />
            </div>
            )}
        </div>
    );
}

/** The wall behind the sign, and the same collage inside the sign's picture panel (`inside`). */
export function ArtWall({ inside = false }) {
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid={inside ? "art-wall-inside" : "art-wall"} aria-hidden="true">
            <div className="absolute inset-0 opacity-[0.5]"
                style={{ backgroundImage: "radial-gradient(#d4d4d8 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
            {WALL.map((p) => <Print key={p.key} p={p} />)}
        </div>
    );
}

